import { Maybe } from 'types/Maybe'
import {
  ChatEventType,
  ChatStreamEvent,
  ChatStreamHandlers,
} from './types'

const EVENT_DELIMITER = /\r?\n\r?\n/

class ChatStreamError extends Error {
  constructor() {
    super('Chat response stream failed')
    this.name = 'ChatStreamError'
    Object.setPrototypeOf(this, ChatStreamError.prototype)
  }
}

const parseEvent = (rawEvent: string): Maybe<ChatStreamEvent> => {
  const data = rawEvent
    .split(/\r?\n/)
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trimStart())
    .join('\n')

  return data ? JSON.parse(data) as ChatStreamEvent : null
}

const handleEvent = (rawEvent: string, { onStarted, onContent }: ChatStreamHandlers) => {
  const event = parseEvent(rawEvent)

  switch (event?.type) {
    case ChatEventType.STARTED:
      if (event.chatId) onStarted(event.chatId)
      return false
    case ChatEventType.CONTENT_DELTA:
      if (event.content) onContent(event.content)
      return false
    case ChatEventType.COMPLETED:
      return true
    case ChatEventType.ERROR:
      throw new ChatStreamError()
    default:
      return false
  }
}

const readChatCompletionStream = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  handlers: ChatStreamHandlers,
) => {
  const decoder = new TextDecoder()
  let buffer = ''
  let isComplete = false

  try {
    while (!isComplete) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read()
      buffer += decoder.decode(value, { stream: !done })

      const rawEvents = buffer.split(EVENT_DELIMITER)
      buffer = done ? '' : rawEvents.pop() || ''
      isComplete = rawEvents.some((rawEvent) => rawEvent.trim() && handleEvent(rawEvent, handlers))

      if (done && !isComplete) throw new Error('Chat stream ended before the completed event')
    }
  } finally {
    if (isComplete) await reader.cancel()
    reader.releaseLock()
  }
}

const resolveApiPath = (path: string) => {
  const apiUrl = process.env.REACT_APP_API_URL

  if (!apiUrl) throw new Error('REACT_APP_API_URL is not configured')

  const baseUrl = new URL(apiUrl, window.location.origin).toString()
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`

  return new URL(path.replace(/^\//, ''), normalizedBaseUrl).toString()
}

export { ChatStreamError, readChatCompletionStream, resolveApiPath }
